/**
 * `progress` → **Customize Progress tracking column** (spec M2 tier-2 §5.15, card P-123).
 *
 * The one dialog in this family whose absence **broke a cell rather than hid a preference**: a
 * progress column is *computed* from the done state of the status columns its settings name, so
 * with no way to name them every progress cell painted `—` (P-123).
 *
 * The capture reaches the row — `editors/progress/settings-dialog.json` is the `Settings`
 * submenu, first entry *Customize Progress tracking column* — and stops. The store is documented
 * and is what the dialog writes, verbatim `[cvs§2 progress]`:
 *
 * ```json
 * {"related_columns": {"isNormalized": true,
 *                      "columns": {"status": {"isSelected": true, "percentage": 100}}}}
 * ```
 *
 * Three rules this keeps:
 *
 *  - **only `status` columns are offered.** `progressPercentage` asks each source column whether
 *    its cell is on a *done* label, and only a status column's own settings know that
 *    (`done_colors`). Offering any other type would create a source that can never be done.
 *  - **unticking keeps the weight.** The entry stays with `isSelected: false`, which
 *    `progressSources` already filters out, so re-ticking a column restores the weight the user
 *    typed instead of silently resetting it.
 *  - **a new source takes an even share of 100.** With `isNormalized` the weights are rescaled on
 *    read anyway (`progressPercentage` divides by their total), so the split is a sensible
 *    starting point rather than a constraint.
 */
import { progressSources, type ProgressSettings } from '@elizmonday/shared';
import {
  HideSummaryField,
  SettingsCheckbox,
  SettingsSection,
  SettingsForm,
  useSettingsDraft,
} from '../settings-kit';
import type { ColumnSettingsProps } from '../types';

type RelatedColumns = NonNullable<ProgressSettings['related_columns']>;
type RelatedColumn = NonNullable<RelatedColumns['columns']>[string];

export function ProgressSettingsDialog({
  column,
  columns = [],
  settings,
  onCommit,
  onCancel,
}: ColumnSettingsProps<ProgressSettings>) {
  const [draft, patch] = useSettingsDraft<ProgressSettings>(settings);
  const related: RelatedColumns = draft.related_columns ?? {};
  const entries: Record<string, RelatedColumn> = related.columns ?? {};
  const sources = columns.filter((entry) => entry.type === 'status');
  const selectedIds = progressSources(draft).map((source) => source.columnId);

  const writeColumns = (next: Record<string, RelatedColumn>): void =>
    patch({ related_columns: { ...related, columns: next } });

  const toggle = (columnId: string, on: boolean): void => {
    if (!on) {
      writeColumns({ ...entries, [columnId]: { ...entries[columnId], isSelected: false } });
      return;
    }
    const count = selectedIds.filter((id) => id !== columnId).length + 1;
    const share = Math.round(100 / count);
    writeColumns({
      ...entries,
      [columnId]: { ...entries[columnId], isSelected: true, percentage: entries[columnId]?.percentage ?? share },
    });
  };

  const weigh = (columnId: string, percentage: number): void =>
    writeColumns({ ...entries, [columnId]: { ...entries[columnId], isSelected: true, percentage } });

  return (
    <SettingsForm
      title="Progress Tracking column settings"
      testId="progress-settings"
      onSave={() => onCommit(draft)}
      onCancel={onCancel}
    >
      <SettingsSection label="Status columns to track" />
      {sources.length === 0 ? (
        <p className="ez-type-settings__empty" data-testid="progress-no-sources">
          This board has no Status column yet, so there is nothing for {column.title} to track.
        </p>
      ) : (
        <ul className="ez-type-settings__list" data-testid="progress-sources">
          {sources.map((source) => {
            const entry = entries[source.id];
            const on = entry?.isSelected === true;
            return (
              <li key={source.id} className="ez-type-settings__list-row" data-testid={`progress-row-${source.id}`}>
                <input
                  type="checkbox"
                  data-testid={`progress-track-${source.id}`}
                  aria-label={`Track ${source.title}`}
                  checked={on}
                  onChange={(event) => toggle(source.id, event.target.checked)}
                />
                <span title={source.title}>{source.title}</span>
                <input
                  type="number"
                  min={0}
                  max={100}
                  data-testid={`progress-weight-${source.id}`}
                  aria-label={`Weight of ${source.title}`}
                  disabled={!on}
                  value={String(entry?.percentage ?? 0)}
                  onChange={(event) => weigh(source.id, Number(event.target.value))}
                />
              </li>
            );
          })}
        </ul>
      )}

      <SettingsCheckbox
        label="Rescale the weights to add up to 100%"
        testId="progress-normalized"
        checked={related.isNormalized ?? true}
        onChange={(next) => patch({ related_columns: { ...related, isNormalized: next } })}
      />

      <HideSummaryField
        testId="progress-hide-footer"
        checked={draft.hide_footer ?? false}
        onChange={(next) => patch({ hide_footer: next })}
      />
    </SettingsForm>
  );
}
